import { useEffect, useRef, useState } from 'react'
import { getGatewayWsUrl } from '../../../utils/deviceGateway'
import { useLiveViewStateStore } from '../LiveViewStore'
import { getCachedAuthToken } from '../api/getCachedAuthToken'

const MAX_RECONNECT_ATTEMPTS = 5
const RECONNECT_DELAY = 5000 // 5 seconds
const MAX_RECONNECT_DELAY = 30000 // 30 seconds
const CONNECTION_TIMEOUT = 10000 // 10 seconds timeout for connection
const STALE_CONNECTION_TIMEOUT = 300000 // 5 minutes - detect stale connections

export const useLiveStream = (baseURL: string, disabled: boolean, idle: boolean) => {
    const { streamStarted, deviceName } = useLiveViewStateStore()

    // Local state instead of buffer store
    const [imgSrc, setImgSrc] = useState('')
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [frameTimeout, setFrameTimeout] = useState<number | null>(null)

    // Refs to maintain state between re-renders
    const wsRef = useRef<WebSocket | null>(null)
    const reconnectAttemptsRef = useRef(0)
    const reconnectTimeoutRef = useRef<NodeJS.Timeout>()
    const connectionTimeoutRef = useRef<NodeJS.Timeout>()
    const staleConnectionTimeoutRef = useRef<NodeJS.Timeout>()
    const currentObjectUrlRef = useRef<string | null>(null)
    const isConnectingRef = useRef(false)
    const shouldConnectRef = useRef(false)
    const isMountedRef = useRef(true)
    const lastFrameTimeRef = useRef<number>(Date.now())
    const frameTimeoutIntervalRef = useRef<NodeJS.Timeout>()
    const frameCountRef = useRef(0)

    const cleanup = () => {
        shouldConnectRef.current = false
        isConnectingRef.current = false

        if (wsRef.current) {
            wsRef.current.close()
            wsRef.current = null
        }
        if (currentObjectUrlRef.current) {
            URL.revokeObjectURL(currentObjectUrlRef.current)
            currentObjectUrlRef.current = null
        }
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current)
            reconnectTimeoutRef.current = undefined
        }
        if (connectionTimeoutRef.current) {
            clearTimeout(connectionTimeoutRef.current)
            connectionTimeoutRef.current = undefined
        }
        if (staleConnectionTimeoutRef.current) {
            clearTimeout(staleConnectionTimeoutRef.current)
            staleConnectionTimeoutRef.current = undefined
        }
        if (frameTimeoutIntervalRef.current) {
            clearInterval(frameTimeoutIntervalRef.current)
            frameTimeoutIntervalRef.current = undefined
        }

        // Set frameTimeout to null when no connection
        setFrameTimeout(null)
    }

    const handleImageUpdate = (blob: Blob) => {
        try {
            if (!isMountedRef.current) return

            // Validate blob
            if (!blob || blob.size === 0) {
                console.warn('Received empty blob')
                return
            }

            const url = URL.createObjectURL(blob)
            const oldUrl = imgSrc

            setImgSrc(url)
            lastFrameTimeRef.current = Date.now()
            frameCountRef.current++

            // Clean up old URL after a short delay to ensure new image is loaded
            if (oldUrl && oldUrl.startsWith('blob:')) {
                setTimeout(() => {
                    URL.revokeObjectURL(oldUrl)
                }, 100)
            }

            currentObjectUrlRef.current = url
        } catch (error) {
            console.error('Error handling image update:', error)
        }
    }

    const startFrameTimeoutCheck = () => {
        if (frameTimeoutIntervalRef.current) {
            clearInterval(frameTimeoutIntervalRef.current)
        }

        frameTimeoutIntervalRef.current = setInterval(() => {
            if (!isMountedRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
                setFrameTimeout(null)
                return
            }

            const now = Date.now()
            const timeSinceLastFrame = Math.floor((now - lastFrameTimeRef.current) / 1000)
            setFrameTimeout(timeSinceLastFrame)

            // If no frames for 5 minutes, consider connection stale and reconnect
            if (timeSinceLastFrame >= 7) {
                console.warn('Connection appears stale, reconnecting...')
                if (wsRef.current) {
                    wsRef.current.close()
                }
            }
        }, 1000)
    }

    const connect = () => {
        if (isConnectingRef.current || !shouldConnectRef.current || !isMountedRef.current) {
            return
        }

        isConnectingRef.current = true

        // Set connection timeout
        connectionTimeoutRef.current = setTimeout(() => {
            if (isConnectingRef.current && wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
                isConnectingRef.current = false
                wsRef.current.close()
                setError('Connection timeout. Retrying...')
                connect() // Retry immediately
            }
        }, CONNECTION_TIMEOUT)

        try {
            const DEVICE_GATWAY_URL = getGatewayWsUrl('/livestream')
            const baseUrlCleaned = baseURL.replace('https://', '').replace('http://', '')

            // Bind handlers to this exact socket instead of wsRef.current: a reconnect can reassign
            // wsRef.current to a new (still CONNECTING) socket, and this socket's onopen sending on
            // that one throws InvalidStateError.
            const ws = new WebSocket(DEVICE_GATWAY_URL)
            wsRef.current = ws

            ws.onopen = () => {
                if (!isMountedRef.current) return

                // Clear connection timeout
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current)
                    connectionTimeoutRef.current = undefined
                }

                isConnectingRef.current = false
                reconnectAttemptsRef.current = 0
                setLoading(false)
                setError(null)
                lastFrameTimeRef.current = Date.now()
                setFrameTimeout(0)
                frameCountRef.current = 0
                startFrameTimeoutCheck()

                const authMessage = {
                    ip: baseUrlCleaned,
                    deviceToken: localStorage.getItem('liveViewAccessToken'),
                    authToken: getCachedAuthToken() || '',
                    device_name: deviceName || 'unknown name'
                }
                ws.send(JSON.stringify(authMessage))
            }

            ws.onclose = (event) => {
                if (!isMountedRef.current) return

                // Clear connection timeout
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current)
                    connectionTimeoutRef.current = undefined
                }

                isConnectingRef.current = false
                if (frameTimeoutIntervalRef.current) {
                    clearInterval(frameTimeoutIntervalRef.current)
                    frameTimeoutIntervalRef.current = undefined
                }

                // Set frameTimeout to null when connection closes
                setFrameTimeout(null)

                // Don't reconnect if it was a clean close or we shouldn't connect
                if (event.code === 1000 || !shouldConnectRef.current) {
                    return
                }

                const delay = Math.min(RECONNECT_DELAY * Math.pow(2, reconnectAttemptsRef.current), MAX_RECONNECT_DELAY)

                if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS && shouldConnectRef.current) {
                    reconnectAttemptsRef.current++
                    setError(`Connection lost. Reconnecting in ${delay / 1000} seconds...`)
                    reconnectTimeoutRef.current = setTimeout(() => {
                        if (shouldConnectRef.current && isMountedRef.current) {
                            connect()
                        }
                    }, delay)
                } else {
                    setError('Connection failed after multiple attempts. Please refresh the page.')
                }
            }

            ws.onerror = (error) => {
                if (!isMountedRef.current) return

                // Clear connection timeout
                if (connectionTimeoutRef.current) {
                    clearTimeout(connectionTimeoutRef.current)
                    connectionTimeoutRef.current = undefined
                }

                isConnectingRef.current = false
                setFrameTimeout(null)
                setError('Connection error occurred')
            }

            ws.onmessage = (event) => {
                if (!isMountedRef.current) return

                try {
                    // Check if data is a blob — text frames are server messages (errors, timeout, etc.)
                    if (!(event.data instanceof Blob)) {
                        let userMessage = 'Stream interrupted — refresh and try again.'
                        try {
                            const parsed = JSON.parse(event.data)
                            const serverMessage: string = parsed.message || ''
                            if (parsed.status === 'timeout') {
                                userMessage = 'Session ended after 45 min — reconnect to continue.'
                            } else if (
                                serverMessage.includes('too many') ||
                                serverMessage.includes('concurrent connections')
                            ) {
                                userMessage =
                                    'Live view unavailable: too many active sessions are open — please close other live view tabs or wait a moment and try again.'
                            } else if (
                                serverMessage.includes('unreachable') ||
                                serverMessage.includes('offline') ||
                                serverMessage.includes('timed out')
                            ) {
                                userMessage = `${serverMessage}: Check device network connection and power supply.`
                            } else if (serverMessage) {
                                userMessage = serverMessage
                            }
                        } catch {
                            /* non-JSON, keep default */
                        }
                        console.warn('Received non-blob data:', userMessage)
                        const { setStreamStarted, setErrorMessage } = useLiveViewStateStore.getState()
                        setErrorMessage(userMessage)
                        setStreamStarted(baseURL, false)
                        return
                    }

                    const blob = new Blob([event.data], { type: 'image/jpeg' })

                    // Validate blob size
                    if (blob.size === 0) {
                        console.warn('Received empty blob')
                        return
                    }

                    handleImageUpdate(blob)
                } catch (error) {
                    console.error('Error processing video stream:', error)
                    setError('Error processing video stream')
                }
            }
        } catch (error) {
            if (!isMountedRef.current) return

            // Clear connection timeout
            if (connectionTimeoutRef.current) {
                clearTimeout(connectionTimeoutRef.current)
                connectionTimeoutRef.current = undefined
            }

            isConnectingRef.current = false
            setFrameTimeout(null)
            setError('Failed to establish connection')
        }
    }

    useEffect(() => {
        const shouldConnect = baseURL && !disabled && !idle && Boolean(streamStarted[baseURL] ?? false)
        shouldConnectRef.current = Boolean(shouldConnect)

        if (!shouldConnect) {
            cleanup()
            return
        }

        connect()

        return cleanup
    }, [baseURL, disabled, idle, streamStarted[baseURL]])

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false
            cleanup()
        }
    }, [])

    return { imgSrc, loading, error, frameTimeout }
}
